/**
 * Tonight — the app (docs/10 §4, docs/11 §3.3). One screen, one fetch:
 * 24 h ahead, every event within the chosen radius, unreachable ones kept.
 *
 *   TripCard      while a plan is active
 *   NextUpCard    the server's best reachable row — countdown if imminent,
 *                 clock time if hours away; with no position, the earliest
 * exit with no timing claims
 *   TimeRail      everything else, in exit-time order, map as a toggle
 *
 * The screen never says "nothing" while anything exists in the next 24 h.
 * A quiet night looks like a quiet night, with the two actions that would
 * change the answer.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Zap, Settings, Locate, LocateOff, MapPin, ChevronDown, Clock } from 'lucide-react';
import { api, ApiError, type Feed, type FeedParams, type Opp, type Metro } from './api.js';
import { headThere, endAttempt } from './attempt.js';
import {
  store, type StoredAttempt, type SearchContext, type Prefs, type SavedPlace, DEFAULT_RADIUS_MI,
} from './storage.js';
import { useForegroundPoll, useMinuteTick, type LocState } from './hooks.js';
import { miToKm, agoSeconds, fmtTime, countdown } from './format.js';
import { StatusLine, EmptyState, Skeleton, Button, InlineWarn, type StatusState } from './ui.js';
import { NextUpCard } from './EventCard.js';
import { TripCard } from './TripCard.js';
import { TimeRail } from './TimeRail.js';
import { WhereSheet } from './WhereSheet.js';
import { OutcomeSheet } from './OutcomeSheet.js';
import { scheduleReminder } from './reminders.js';

const MAX_RADIUS_MI = 25;
/** A week: drivers plan the shift and the weekend (docs/10 J1). */
const HORIZON_MIN = 7 * 24 * 60;

export function HomeScreen({
  name, driverMode, origin, locState, fixedAt, onRetryLocation, search, onSearchChange, prefs, metros,
  onOpenDetail, onOpenSettings, detailResult, onDetailResultConsumed,
}: {
  name: string | null;
  driverMode: string;
  origin: { lat: number; lng: number } | null;
  locState: LocState;
  fixedAt: number | null;
  onRetryLocation: () => void;
  search: SearchContext;
  onSearchChange: (s: SearchContext) => void;
  prefs: Prefs;
  metros: Metro[];
  onOpenDetail: (opp: Opp, feed: Feed) => void;
  onOpenSettings: () => void;
  /** Detail screen's "Head there" lands here so the TripCard appears in place. */
  detailResult: { attempt: StoredAttempt } | null;
  onDetailResultConsumed: () => void;
}) {
  const now = useMinuteTick();
  const [feed, setFeed] = useState<Feed | null>(null);
  const [fetchedAt, setFetchedAt] = useState<number | null>(null);
  const [offline, setOffline] = useState(false);
  const [noMetro, setNoMetro] = useState(false);
  const [sheet, setSheet] = useState(false);
  const [attempt, setAttempt] = useState<StoredAttempt | null>(store.attempt.get());
  const [outcomeFor, setOutcomeFor] = useState<StoredAttempt | null>(null);
  const [notice, setNotice] = useState<{ text: string; switchTo?: Opp } | null>(null);
  const [busy, setBusy] = useState(false);
  const [reminded, setReminded] = useState<string | null>(null);
  const announceRef = useRef('');
  const lastTopRef = useRef<string | null>(null);
  const [, bump] = useState(0);

  useEffect(() => {
    if (detailResult) { setAttempt(detailResult.attempt); onDetailResultConsumed(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailResult]);

  // Location off: search around the service area the driver chose on Welcome
  // (Providence by default), never whichever metro sorts first.
  const fallbackMetro = metros.find((m) => m.slug === (store.flags.get().welcomeMetro ?? 'pvd')) ?? metros[0] ?? null;
  const center = search.center.kind === 'gps'
    ? origin ?? (locState === 'denied' && fallbackMetro ? fallbackMetro.centroid : null)
    : { lat: search.center.place.lat, lng: search.center.place.lng };
  const centerKey = center ? `${center.lat.toFixed(3)},${center.lng.toFixed(3)}` : null;

  // ---- feed ---------------------------------------------------------------
  const refresh = useCallback(async () => {
    if (!center) return;
    try {
      const f = await api.feedQuery({
        origin: origin ?? undefined,
        center,
        radius_km: miToKm(MAX_RADIUS_MI),
        driver_mode: driverMode as FeedParams['driver_mode'],
        limit: 120,
        horizon_min: HORIZON_MIN,
        include_unreachable: true,
      });
      setFeed(f); setFetchedAt(Date.now()); setOffline(false); setNoMetro(false);
      store.feed.set(f);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'no_metro') { setNoMetro(true); setFeed(null); return; }
      if (err instanceof ApiError && err.status === 401) { window.location.reload(); return; }
      const cached = store.feed.get();
      if (cached && !feed) { setFeed(cached.feed); setFetchedAt(cached.at); }
      setOffline(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [centerKey, origin?.lat, origin?.lng, driverMode]);

  useForegroundPoll(() => { void refresh(); }, 60_000, center !== null, `${centerKey}|${driverMode}`);

  // ---- plan lifecycle -----------------------------------------------------
  useEffect(() => {
    if (attempt && new Date(attempt.expires_at).getTime() < now) {
      setOutcomeFor(attempt); setAttempt(null); store.attempt.clear();
    }
  }, [attempt, now]);

  async function go(opp: Opp) {
    if (!feed) return;
    if (attempt) {
      setNotice({ text: `You’re already heading to ${attempt.title}.`, switchTo: opp });
      return;
    }
    setBusy(true);
    const r = await headThere(feed.decision_id, opp);
    setBusy(false);
    if (r.ok) {
      setAttempt(r.attempt); setNotice(null);
      announceRef.current = `Heading to ${r.attempt.title}.`;
      if (prefs.reminders && r.attempt.leave_by) scheduleReminder(r.attempt);
    } else if (r.reason === 'already_heading') {
      setNotice({ text: r.message, switchTo: opp });
    } else {
      setNotice({ text: r.message });
    }
  }

  async function switchTo(opp: Opp) {
    if (!attempt) return;
    setBusy(true);
    await endAttempt(attempt.id, 'cancelled');
    setAttempt(null); setNotice(null); setBusy(false);
    await go(opp);
  }

  async function finish(reason: 'completed' | 'cancelled') {
    if (!attempt) return;
    setBusy(true);
    const a = attempt;
    await endAttempt(a.id, reason);
    setAttempt(null); setBusy(false);
    announceRef.current = reason === 'completed' ? 'Trip ended.' : 'Trip cancelled.';
    if (reason === 'completed') setOutcomeFor(a);
    void refresh();
  }

  function remind(opp: Opp) {
    if (!opp.leave_by) return;
    void scheduleReminder({
      id: opp.decision_candidate_id, title: opp.occurrence.venue_name ?? opp.occurrence.title,
      staging_label: opp.staging.label, leave_by: opp.leave_by,
    }).then((ok) => { if (ok) setReminded(opp.decision_candidate_id); else setNotice({ text: 'Reminders are blocked for Surge in your browser settings.' }); });
  }

  // ---- derive the screen --------------------------------------------------
  const inRadius = (feed?.opportunities ?? []).filter((o) => o.distance_km <= miToKm(search.radiusMi));
  const hasOrigin = origin !== null;
  // The trip's own event lives on the TripCard, never in the rail.
  const others = inRadius.filter((o) => !(attempt && o.occurrence.id === attempt.occurrence_id));
  const nextUp: Opp | null = attempt || others.length === 0 ? null
    : hasOrigin
      ? (others.find((o) => o.reachable) ?? [...others].sort(byP50)[0]!)
      : [...others].sort(byP50)[0]!;
  const rail = [...others].filter((o) => o !== nextUp).sort(byP50);
  const ageMs = fetchedAt ? now - fetchedAt : null;

  // aria-live only on MATERIAL change: a new Next up as actually displayed
  // (after the radius filter), never the 60 s refresh (docs/11 §5.6).
  useEffect(() => {
    const id = nextUp?.occurrence.id ?? null;
    if (id && id !== lastTopRef.current) {
      lastTopRef.current = id;
      announceRef.current = lastAnnouncement(nextUp!, now);
      bump((n) => n + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextUp?.occurrence.id]);

  const status: StatusState =
    offline && fetchedAt ? { kind: 'offline', asOf: fmtTime(new Date(fetchedAt).toISOString()), onAction: () => void refresh() }
      : locState === 'denied' && search.center.kind === 'gps' ? { kind: 'location-off', onAction: onRetryLocation }
        : !center && locState === 'unknown' ? { kind: 'finding' }
          : ageMs !== null && ageMs > 150_000 ? { kind: 'stale', agoLabel: agoSeconds(fetchedAt!, now), onAction: () => void refresh() }
            : { kind: 'none' };

  const chipLabel = search.center.kind === 'gps'
    ? (locState === 'denied' ? (fallbackMetro?.name ?? 'Service area') : 'Near me')
    : search.center.place.label;
  const chipRadius = search.radiusMi !== DEFAULT_RADIUS_MI ? ` · ${search.radiusMi} mi` : '';
  const greeting = attempt ? `Heading to ${attempt.title}`
    : feed ? (inRadius.length === 0 ? `Tonight${name ? `, ${name}` : ''} · nothing within ${search.radiusMi} mi`
      : `Tonight${name ? `, ${name}` : ''} · ${inRadius.length} crowd${inRadius.length === 1 ? '' : 's'} within ${search.radiusMi} mi`)
      : `Tonight${name ? `, ${name}` : ''}`;

  const headingFor = (occurrenceId: string) =>
    feed?.opportunities.find((o) => o.occurrence.id === occurrenceId)?.heading ?? null;

  const currentMetroSlug = search.center.kind === 'place' && search.center.place.id.startsWith('metro:')
    ? search.center.place.id.slice(6) : (locState === 'denied' ? fallbackMetro?.slug : undefined);
  const otherMetro = metros.find((m) => m.slug !== currentMetroSlug);

  return (
    <div className="screen">
      <header className="hdr">
        <span className="brand"><span className="mark"><Zap size={18} aria-hidden="true" /></span>Surge</span>
        <span className="hdr-spacer" />
        <button className={`where-chip${locState === 'denied' && search.center.kind === 'gps' ? ' warn' : ''}`}
          onClick={() => setSheet(true)} aria-haspopup="dialog"
          aria-label={`Searching ${chipLabel}${chipRadius}. Change where to look`}>
          {search.center.kind === 'gps'
            ? (locState === 'denied' ? <LocateOff size={18} aria-hidden="true" /> : <Locate size={18} aria-hidden="true" />)
            : <MapPin size={18} aria-hidden="true" />}
          <span className="label">{chipLabel}{chipRadius}</span>
          <ChevronDown size={16} aria-hidden="true" />
        </button>
        <button className="icon-btn" onClick={onOpenSettings} aria-label="Settings"><Settings size={22} aria-hidden="true" /></button>
      </header>
      <p className="greeting">{greeting}</p>
      <div className="rule" />

      <StatusLine state={status} />

      {notice && (
        <div className="card" role="alert" style={{ gap: 10 }}>
          <InlineWarn>{notice.text}</InlineWarn>
          <div className="btn-row">
            {notice.switchTo && (
              <Button variant="primary" busy={busy} onClick={() => void switchTo(notice.switchTo!)}>Switch to this</Button>
            )}
            <Button variant="secondary" onClick={() => setNotice(null)}>{notice.switchTo ? `Keep ${attempt?.title ?? 'current'}` : 'OK'}</Button>
          </div>
        </div>
      )}

      {attempt && (
        <TripCard attempt={attempt} now={now} maps={prefs.maps} heading={headingFor(attempt.occurrence_id)}
          busy={busy} onHere={() => void finish('completed')} onDidntGo={() => void finish('cancelled')} />
      )}

      {!feed && center && !noMetro && (
        <>
          <Skeleton variant="card" />
          <Skeleton variant="row" />
          <Skeleton variant="row" />
        </>
      )}

      {!center && locState === 'denied' && (
        <EmptyState icon={<LocateOff size={48} aria-hidden="true" />} title="Where should we look?"
          body="Location is off for Surge. Pick a place or a service area and we’ll show the night around it.">
          <div className="btn-row"><Button variant="primary" onClick={() => setSheet(true)}>Choose a place</Button></div>
        </EmptyState>
      )}

      {noMetro && center && (
        <EmptyState icon={<MapPin size={48} aria-hidden="true" />} title="Outside the service area"
          body={`Surge isn’t live within ${search.radiusMi} mi of ${chipLabel === 'Near me' ? 'you' : chipLabel} yet.`}>
          <div className="btn-row">
            {metros.map((m) => (
              <Button key={m.slug} variant="secondary"
                onClick={() => onSearchChange({ ...search, center: { kind: 'place', place: { id: `metro:${m.slug}`, label: m.name, detail: 'Service area', lat: m.centroid.lat, lng: m.centroid.lng } } })}>
                Try {m.name}
              </Button>
            ))}
          </div>
        </EmptyState>
      )}

      {feed && !noMetro && inRadius.length === 0 && (
        <EmptyState icon={<Clock size={48} aria-hidden="true" />} title="Quiet night around here"
          body={`No crowds expected within ${search.radiusMi} mi this week. We check every minute.`}>
          <div className="btn-row">
            {search.radiusMi < MAX_RADIUS_MI && (
              <Button variant="secondary" onClick={() => onSearchChange({ ...search, radiusMi: MAX_RADIUS_MI })}>
                Search {MAX_RADIUS_MI} mi
              </Button>
            )}
            {otherMetro && (
              <Button variant="secondary"
                onClick={() => onSearchChange({ ...search, center: { kind: 'place', place: { id: `metro:${otherMetro.slug}`, label: otherMetro.name, detail: 'Service area', lat: otherMetro.centroid.lat, lng: otherMetro.centroid.lng } satisfies SavedPlace } })}>
                Try {otherMetro.name}
              </Button>
            )}
          </div>
          {fetchedAt && <p className="small muted">Updated {agoSeconds(fetchedAt, now)}</p>}
        </EmptyState>
      )}

      {feed && !noMetro && inRadius.length > 0 && (
        <div className="two-col">
          <div className="screen" style={{ flex: 'none' }}>
            {nextUp && !attempt && (
              <NextUpCard opp={nextUp} now={now} units={prefs.units} hasOrigin={hasOrigin} tripActive={false}
                onHeadThere={() => void go(nextUp)} onDetails={() => onOpenDetail(nextUp, feed)}
                onRemind={() => remind(nextUp)} reminded={reminded === nextUp.decision_candidate_id}
                onReadAgain={() => { announceRef.current = ''; bump((n) => n + 1); setTimeout(() => { announceRef.current = lastAnnouncement(nextUp, now); bump((n) => n + 1); }, 50); }} />
            )}
          </div>
          <div className="screen" style={{ flex: 'none' }}>
            {inRadius.length > 0 && (
              <TimeRail rows={rail} pins={inRadius} now={now} units={prefs.units} hasOrigin={hasOrigin}
                center={center} radiusMi={search.radiusMi} origin={origin}
                onOpen={(o) => onOpenDetail(o, feed)}
                footer={
                  <>
                    <p className="rail-foot">That’s everything within {search.radiusMi} mi this week.</p>
                    {search.radiusMi < MAX_RADIUS_MI && (
                      <Button variant="text" onClick={() => onSearchChange({ ...search, radiusMi: MAX_RADIUS_MI })}>Search further out</Button>
                    )}
                  </>
                } />
            )}

          </div>
        </div>
      )}

      {sheet && (
        <WhereSheet search={search} locState={locState} bias={origin ?? center} metros={metros}
          count={feed ? inRadius.length : null} onChange={onSearchChange} onClose={() => setSheet(false)} />
      )}

      {outcomeFor && <OutcomeSheet attempt={outcomeFor} onDone={() => setOutcomeFor(null)} />}

      <div aria-live="polite" className="visually-hidden">{announceRef.current}</div>
    </div>
  );
}

function byP50(a: Opp, b: Opp): number { return a.egress.p50_at.localeCompare(b.egress.p50_at); }

function lastAnnouncement(opp: Opp, now: number): string {
  const v = opp.occurrence.venue_name ?? opp.occurrence.title;
  return opp.leave_by
    ? `Next up: ${v}. Leave ${countdown(opp.leave_by, now).label}, by ${fmtTime(opp.leave_by)}. Pickup ${opp.staging.label}.`
    : `Next up: ${v}, crowd out at ${fmtTime(opp.egress.p10_at)}.`;
}
